// ========================================
// THE QUIET GROVE - MOTION ENGINE
// Cursor, nav scroll, curtain intro, scroll reveals,
// magnetic buttons, hero parallax, gallery lightbox,
// variant pills, detail tabs, cart drawer, filter bar.
// Emil Kowalski design engineering principles throughout.
// ========================================

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// --- CUSTOM CURSOR ---
const dot = document.getElementById('cursorDot');
const ring = document.getElementById('cursorRing');
let mouseX = 0, mouseY = 0, ringX = 0, ringY = 0;

if (window.matchMedia('(pointer: fine)').matches) {
  document.addEventListener('mousemove', (e) => {
    mouseX = e.clientX; mouseY = e.clientY;
    dot.style.transform = `translate(${mouseX - 3}px, ${mouseY - 3}px)`;
  });
  function animateRing() {
    ringX += (mouseX - ringX) * 0.15;
    ringY += (mouseY - ringY) * 0.15;
    ring.style.transform = `translate(${ringX - 18}px, ${ringY - 18}px)`;
    requestAnimationFrame(animateRing);
  }
  animateRing();

  // Delegate hover detection (handles dynamically added elements)
  document.addEventListener('mouseover', (e) => {
    if (e.target.closest('[data-cursor="hover"], a, button, .card, .filter-pill, .variant-pill')) {
      ring.classList.add('hovering');
    }
  });
  document.addEventListener('mouseout', (e) => {
    if (e.target.closest('[data-cursor="hover"], a, button, .card, .filter-pill, .variant-pill')) {
      ring.classList.remove('hovering');
    }
  });
}

// --- NAV SCROLL BLUR ---
const header = document.querySelector('.site-header');
if (header) {
  window.addEventListener('scroll', () => {
    header.classList.toggle('scrolled', window.scrollY > 40);
  }, { passive: true });
}

// --- MOBILE NAV TOGGLE ---
function initMobileNav() {
  const toggle = document.getElementById('mobileNavToggle');
  const closeBtn = document.getElementById('mobileNavClose');
  const nav = document.getElementById('primaryNav');
  const overlay = document.getElementById('mobileNavOverlay');
  if (!toggle || !nav) return;

  function open() {
    nav.classList.add('mobile-open');
    if (overlay) overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function close() {
    nav.classList.remove('mobile-open');
    if (overlay) overlay.classList.remove('open');
    document.body.style.overflow = '';
  }

  toggle.addEventListener('click', open);
  if (closeBtn) closeBtn.addEventListener('click', close);
  if (overlay) overlay.addEventListener('click', close);

  // Close when a link is tapped
  nav.querySelectorAll('a').forEach(link => {
    link.addEventListener('click', close);
  });
}

// --- PAGE CURTAIN + HERO REVEALS ---
// Only fire the curtain on the homepage. Other pages get instant reveals.
function initCurtain() {
  const curtain = document.getElementById('curtain');
  if (!curtain) return;

  const isHomepage = window.location.pathname === '/' || window.location.pathname === '';

  if (reduceMotion || typeof gsap === 'undefined' || !isHomepage) {
    // No curtain - just reveal everything immediately
    curtain.style.display = 'none';
    document.querySelectorAll('.reveal, .reveal-up, .reveal-fade, .reveal-scale, .hero-text > *, .hero-art, .hero-badge').forEach(el => {
      el.style.opacity = '1'; el.style.transform = 'none';
    });
    // Still register plugins for scroll reveals
    if (typeof gsap !== 'undefined' && !reduceMotion) {
      gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
    }
    return;
  }

  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

  // Faster curtain: 1.2s total instead of 2.5s
  const tl = gsap.timeline();
  tl.to(curtain.querySelector('.curtain-text'), { opacity: 1, duration: 0.3, ease: 'power2.out' })
    .to(curtain.querySelector('.curtain-text'), { opacity: 0, duration: 0.25, delay: 0.3, ease: 'power2.in' })
    .to(curtain, { y: '-100%', duration: 0.5, ease: 'power3.inOut' }, '-=0.1')
    .to(curtain, { display: 'none' });

  // Hero reveals - staggered, ease-out for entering elements
  const heroText = document.querySelector('.hero-text');
  if (heroText) {
    const heroChildren = heroText.children;
    tl.fromTo(heroChildren, { opacity: 0, y: 30 }, {
      opacity: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.12
    }, '-=0.3');
  }

  const heroArt = document.querySelector('.hero-art');
  if (heroArt) {
    tl.fromTo(heroArt, { opacity: 0, scale: 0.95 }, { opacity: 1, scale: 1, duration: 1.0, ease: 'power3.out' }, '-=0.8');
  }

  const heroBadge = document.querySelector('.hero-badge');
  if (heroBadge) {
    tl.fromTo(heroBadge, { opacity: 0, y: 20, scale: 0.9 }, { opacity: 1, y: 0, scale: 1, duration: 0.5, ease: 'power3.out' }, '-=0.4');
  }
}

// --- HERO PARALLAX ---
function initHeroParallax() {
  if (reduceMotion || typeof gsap === 'undefined') return;

  const heroArt = document.querySelector('.hero-art .art img');
  if (heroArt) {
    gsap.to(heroArt, {
      yPercent: 12, ease: 'none',
      scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 1 }
    });
  }

  const heroBadge = document.querySelector('.hero-badge');
  if (heroBadge) {
    gsap.to(heroBadge, {
      yPercent: -30, ease: 'none',
      scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 1 }
    });
  }
}

// --- SCROLL REVEALS ---
function initScrollReveals() {
  if (reduceMotion || typeof gsap === 'undefined') return;
  gsap.registerPlugin(ScrollTrigger);

  gsap.utils.toArray('.reveal-up').forEach((el) => {
    gsap.to(el, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 85%' } });
  });
  gsap.utils.toArray('.reveal-fade').forEach((el) => {
    gsap.to(el, { opacity: 1, duration: 0.7, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 85%' } });
  });
  gsap.utils.toArray('.reveal-scale').forEach((el) => {
    gsap.to(el, { opacity: 1, scale: 1, duration: 0.8, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 85%' } });
  });
  gsap.utils.toArray('.reveal').forEach((el) => {
    gsap.to(el, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 90%' } });
  });

  // Product card stagger
  gsap.utils.toArray('.card').forEach((card, i) => {
    gsap.to(card, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out',
      delay: (i % 4) * 0.08,
      scrollTrigger: { trigger: card, start: 'top 90%' } });
  });

  // Value items stagger
  gsap.utils.toArray('.value-item').forEach((item, i) => {
    gsap.to(item, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
      delay: i * 0.1,
      scrollTrigger: { trigger: '.values', start: 'top 80%' } });
  });
}

// --- SMOOTH SCROLL FOR ANCHOR LINKS ---
document.querySelectorAll('a[href^="#"]').forEach(link => {
  link.addEventListener('click', (e) => {
    const href = link.getAttribute('href');
    if (href === '#' || href.length < 2) return;
    const target = document.querySelector(href);
    if (target) {
      e.preventDefault();
      if (typeof gsap !== 'undefined' && !reduceMotion) {
        gsap.to(window, { duration: 1, scrollTo: { y: target, offsetY: 80 }, ease: 'power3.inOut' });
      } else {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }
  });
});

// --- MAGNETIC HOVER ON BUTTONS ---
if (window.matchMedia('(pointer: fine)').matches && !reduceMotion) {
  document.querySelectorAll('.btn, .cart').forEach(btn => {
    btn.addEventListener('mousemove', (e) => {
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      btn.style.transform = `translate(${x * 0.15}px, ${y * 0.2}px)`;
    });
    btn.addEventListener('mouseleave', () => {
      btn.style.transform = 'translate(0, 0)';
    });
  });
}

// --- GALLERY LIGHTBOX ---
function initLightbox() {
  const media = document.querySelector('.product__media');
  if (!media) return;

  // Create lightbox elements
  const lightbox = document.createElement('div');
  lightbox.className = 'lightbox';
  lightbox.id = 'lightbox';
  lightbox.innerHTML = `
    <button class="lightbox-close" onclick="closeLightbox()">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
    </button>
    <button class="lightbox-nav prev" onclick="navLightbox(-1)">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
    </button>
    <button class="lightbox-nav next" onclick="navLightbox(1)">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
    </button>
    <img class="lightbox-img" id="lightboxImg" src="" alt="">
    <div class="lightbox-counter" id="lightboxCounter">1 / 1</div>
  `;
  document.body.appendChild(lightbox);

  // Add zoom hint
  const zoomHint = document.createElement('div');
  zoomHint.className = 'gallery-zoom-hint';
  zoomHint.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4-4M11 8v6M8 11h6"/></svg> Click to enlarge`;
  media.appendChild(zoomHint);

  const galleryImgs = media.querySelectorAll('.gallery__img');
  let currentLbIndex = 0;

  media.addEventListener('click', (e) => {
    if (e.target.closest('.product__thumb')) return;
    const activeImg = media.querySelector('.gallery__img.is-active') || galleryImgs[0];
    currentLbIndex = Array.from(galleryImgs).indexOf(activeImg);
    if (currentLbIndex < 0) currentLbIndex = 0;
    openLightbox();
  });

  function openLightbox() {
    const img = galleryImgs[currentLbIndex];
    if (!img) return;
    document.getElementById('lightboxImg').src = img.src;
    document.getElementById('lightboxCounter').textContent = `${currentLbIndex + 1} / ${galleryImgs.length}`;
    lightbox.classList.add('open');
  }

  window.closeLightbox = function() { lightbox.classList.remove('open'); };

  window.navLightbox = function(dir) {
    currentLbIndex = (currentLbIndex + dir + galleryImgs.length) % galleryImgs.length;
    openLightbox();
  };

  lightbox.addEventListener('click', (e) => {
    if (e.target === lightbox) closeLightbox();
  });

  document.addEventListener('keydown', (e) => {
    if (!lightbox.classList.contains('open')) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowLeft') navLightbox(-1);
    if (e.key === 'ArrowRight') navLightbox(1);
  });

  // Touch swipe support for mobile
  let touchStartX = 0;
  let touchEndX = 0;

  lightbox.addEventListener('touchstart', (e) => {
    touchStartX = e.changedTouches[0].screenX;
  }, { passive: true });

  lightbox.addEventListener('touchend', (e) => {
    touchEndX = e.changedTouches[0].screenX;
    const diff = touchStartX - touchEndX;
    // Swipe threshold: 50px minimum
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        navLightbox(1);  // Swipe left = next
      } else {
        navLightbox(-1); // Swipe right = prev
      }
    }
  }, { passive: true });
}

// --- VARIANT PILLS (replace select with pills) ---
function initVariantPills() {
  const select = document.querySelector('.product__form select[name="id"]');
  if (!select) return;

  const options = Array.from(select.options);
  const wrapper = document.createElement('div');
  wrapper.className = 'variant-pills';

  const priceEl = document.querySelector('[data-price]');

  options.forEach((opt, i) => {
    const pill = document.createElement('button');
    pill.type = 'button';
    pill.className = 'variant-pill' + (opt.selected ? ' active' : '');
    pill.textContent = opt.textContent.replace(/\s*\(sold out\)/, '').split(',')[0].trim();
    if (opt.disabled) { pill.style.opacity = '0.4'; pill.style.pointerEvents = 'none'; }
    pill.addEventListener('click', () => {
      wrapper.querySelectorAll('.variant-pill').forEach(p => p.classList.remove('active'));
      pill.classList.add('active');
      select.selectedIndex = i;
      select.dispatchEvent(new Event('change'));

      // Update the displayed price
      const priceAttr = opt.dataset.price;
      if (priceEl && priceAttr) {
        priceEl.textContent = priceAttr;
      }

      // Remove "From" prefix since a specific variant is now selected
      const priceContainer = document.getElementById('productPrice');
      if (priceContainer && priceContainer.textContent.includes('From')) {
        const span = priceContainer.querySelector('[data-price]');
        priceContainer.innerHTML = '';
        priceContainer.appendChild(span);
      }
    });
    wrapper.appendChild(pill);
  });

  select.style.display = 'none';
  select.parentNode.insertBefore(wrapper, select.nextSibling);

  // Also listen to select changes (e.g. from Shopify's payment button)
  select.addEventListener('change', () => {
    const opt = select.options[select.selectedIndex];
    const priceAttr = opt.dataset.price;
    if (priceEl && priceAttr) {
      priceEl.textContent = priceAttr;
    }
    // Update active pill
    wrapper.querySelectorAll('.variant-pill').forEach((p, i) => {
      p.classList.toggle('active', i === select.selectedIndex);
    });
    // Remove "From" prefix
    const priceContainer = document.getElementById('productPrice');
    if (priceContainer && priceContainer.textContent.includes('From')) {
      const span = priceContainer.querySelector('[data-price]');
      priceContainer.innerHTML = '';
      priceContainer.appendChild(span);
    }
  });
}

// --- QUANTITY CONTROLS ---
function initQuantity() {
  const decrement = document.querySelector('[data-qty-decrement]');
  const increment = document.querySelector('[data-qty-increment]');
  const input = document.querySelector('[data-qty-input]');
  if (!input) return;

  if (decrement) {
    decrement.addEventListener('click', () => {
      const val = Math.max(1, parseInt(input.value) - 1);
      input.value = val;
    });
  }
  if (increment) {
    increment.addEventListener('click', () => {
      const val = parseInt(input.value) + 1;
      input.value = val;
    });
  }
}

// --- DETAIL TABS ---
function initDetailTabs() {
  const tabs = document.querySelectorAll('.detail-tab');
  if (tabs.length === 0) return;
  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.detail-tab').forEach(t => t.classList.remove('active'));
      document.querySelectorAll('.detail-panel').forEach(p => p.classList.remove('active'));
      tab.classList.add('active');
      const panel = document.getElementById('panel-' + tab.dataset.tab);
      if (panel) panel.classList.add('active');
    });
  });
}

// --- COLLECTION FILTER BAR ---
function initFilterBar() {
  const filterBar = document.querySelector('.filter-bar');
  if (!filterBar) return;

  const pills = filterBar.querySelectorAll('.filter-pill');
  const cards = document.querySelectorAll('.collection-grid .card');
  const countEl = filterBar.querySelector('.filter-count');

  pills.forEach(pill => {
    pill.addEventListener('click', () => {
      pills.forEach(p => p.classList.remove('active'));
      pill.classList.add('active');
      const filter = pill.dataset.filter;
      let visible = 0;

      cards.forEach(card => {
        const type = card.dataset.type || '';
        const collection = card.dataset.collection || '';
        const show = filter === 'all' || type === filter || collection === filter;
        card.style.display = show ? '' : 'none';
        if (show) visible++;
      });

      if (countEl) countEl.textContent = `${visible} ${visible === 1 ? 'piece' : 'pieces'}`;
    });
  });
}

// --- CART DRAWER ---
function initCartDrawer() {
  const cartLink = document.querySelector('.cart[href*="cart"]');
  if (!cartLink) return;

  // Create drawer elements
  const overlay = document.createElement('div');
  overlay.className = 'cart-overlay';
  overlay.id = 'cartOverlay';
  document.body.appendChild(overlay);

  const drawer = document.createElement('aside');
  drawer.className = 'cart-drawer';
  drawer.id = 'cartDrawer';
  drawer.innerHTML = `
    <div class="cart-drawer-header">
      <h3>Your cart</h3>
      <button class="cart-drawer-close" id="cartDrawerClose">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
    </div>
    <div class="cart-drawer-body" id="cartDrawerBody">
      <div style="text-align:center;padding:3rem 1rem;color:var(--ink-3);">
        <p>Loading cart...</p>
      </div>
    </div>
    <div class="cart-drawer-footer">
      <div class="cart-drawer-total">
        <span>Subtotal</span>
        <span id="cartDrawerTotal">-</span>
      </div>
      <a href="/cart" class="cart-drawer-checkout">View cart & checkout</a>
    </div>
  `;
  document.body.appendChild(drawer);

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function openDrawer() {
    drawer.classList.remove('closing');
    drawer.classList.add('open');
    overlay.classList.add('open');
    // Fetch cart contents via AJAX
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const body = document.getElementById('cartDrawerBody');
        if (cart.item_count === 0) {
          body.innerHTML = '<div style="text-align:center;padding:3rem 1rem;color:var(--ink-3);"><p>Your cart is empty.</p></div>';
        } else {
          body.innerHTML = cart.items.map(item => `
            <div style="display:flex;gap:0.875rem;padding:0.875rem 0;border-bottom:1px solid var(--stone);">
              <img src="${item.image}" alt="${item.title}" style="width:64px;height:80px;object-fit:cover;border-radius:6px;flex-shrink:0;">
              <div style="flex:1;">
                <div style="font-size:0.8125rem;font-weight:500;color:var(--ink);margin-bottom:0.125rem;">${item.product_title}</div>
                <div style="font-size:0.7rem;color:var(--ink-3);">${item.variant_title || ''}</div>
                <div style="display:flex;justify-content:space-between;align-items:center;margin-top:0.5rem;">
                  <span style="font-size:0.8125rem;color:var(--ink-2);">Qty: ${item.quantity}</span>
                  <span style="font-size:0.8125rem;font-weight:500;color:var(--moss);">${formatMoney(item.final_line_price)}</span>
                </div>
              </div>
            </div>
          `).join('');
        }
        document.getElementById('cartDrawerTotal').textContent = formatMoney(cart.total_price);
        // Update cart count in header
        const countEl = document.querySelector('.cart-count');
        if (countEl) countEl.textContent = cart.item_count;
      })
      .catch(() => {
        document.getElementById('cartDrawerBody').innerHTML = '<div style="text-align:center;padding:3rem 1rem;"><a href="/cart" style="color:var(--moss);">View cart page</a></div>';
      });
  }

  function closeDrawer() {
    drawer.classList.remove('open');
    drawer.classList.add('closing');
    overlay.classList.remove('open');
    setTimeout(() => drawer.classList.remove('closing'), 400);
  }

  cartLink.addEventListener('click', (e) => {
    e.preventDefault();
    openDrawer();
  });

  overlay.addEventListener('click', closeDrawer);
  document.getElementById('cartDrawerClose').addEventListener('click', closeDrawer);

  // Intercept add-to-cart forms: POST via fetch, open drawer, prevent page redirect
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!form.matches('form[action*="cart/add"]')) return;

    e.preventDefault();
    const formData = new FormData(form);
    const submitBtn = form.querySelector('[name="add"]');
    const originalText = submitBtn ? submitBtn.textContent : '';

    if (submitBtn) {
      submitBtn.textContent = 'Adding...';
      submitBtn.disabled = true;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(r => {
        if (!r.ok) throw new Error('Add to cart failed');
        return r.json();
      })
      .then(() => {
        if (submitBtn) {
          submitBtn.textContent = 'Added';
          submitBtn.style.background = 'var(--moss)';
        }
        setTimeout(() => {
          if (submitBtn) {
            submitBtn.textContent = originalText;
            submitBtn.style.background = '';
            submitBtn.disabled = false;
          }
          openDrawer();
        }, 400);
      })
      .catch(() => {
        if (submitBtn) {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
        // Fallback: submit the form normally
        form.submit();
      });
  });
}

// --- INIT EVERYTHING ---
function init() {
  initMobileNav();
  initCurtain();
  initHeroParallax();
  initScrollReveals();
  initLightbox();
  initVariantPills();
  initQuantity();
  initDetailTabs();
  initFilterBar();
  initCartDrawer();

  // GSAP fallback: if GSAP hasn't loaded within 3 seconds, show everything
  setTimeout(() => {
    if (typeof gsap === 'undefined') {
      document.querySelectorAll('.reveal, .reveal-up, .reveal-fade, .reveal-scale, .hero-text > *, .hero-art, .hero-badge, .card').forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      const curtain = document.getElementById('curtain');
      if (curtain) curtain.style.display = 'none';
    }
  }, 3000);
}

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Re-init reveals when GSAP loads (it's deferred)
window.addEventListener('load', () => {
  if (typeof gsap !== 'undefined' && !reduceMotion) {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
    ScrollTrigger.refresh();
  }
});
